/**
 * Session Manager Hook
 * Implements comprehensive session management with:
 * - 2-hour active session timeout
 * - Proactive token refresh (5 min before expiry)
 * - Session timeout warnings
 * - Activity-based session extension
 * - Graceful error handling
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { useAuthStore } from '@/store/auth-store';
import { toast } from 'sonner';

const REFRESH_BEFORE_EXPIRY_MS = 5 * 60 * 1000; // Refresh 5 min before expiry
const WARNING_BEFORE_EXPIRY_MS = 10 * 60 * 1000; // Warn 10 min before expiry
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart'];

interface SessionInfo {
  expiresAt: number | null;
  warningShown: boolean;
  lastActivity: number;
}

export function useSessionManager() {
  const { isAuthenticated, refreshAuth, logout } = useAuthStore();
  const [sessionInfo, setSessionInfo] = useState<SessionInfo>({
    expiresAt: null,
    warningShown: false,
    lastActivity: Date.now(),
  });

  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
  const activityTimerRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Calculate token expiration time from JWT
   */
  const getTokenExpiration = useCallback((): number | null => {
    const token = localStorage.getItem('accessToken');
    if (!token) return null;

    try {
      // Decode JWT (without verification, just to read payload)
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );

      const payload = JSON.parse(jsonPayload);
      return payload.exp * 1000; // Convert to milliseconds
    } catch (error) {
      console.error('Failed to decode token:', error);
      return null;
    }
  }, []);

  /**
   * Refresh token proactively
   */
  const refreshToken = useCallback(async () => {
    try {
      console.log('[Session] Refreshing access token...');
      await refreshAuth();

      const newExpiration = getTokenExpiration();
      setSessionInfo((prev) => ({
        ...prev,
        expiresAt: newExpiration,
        warningShown: false,
      }));

      // Schedule next refresh
      scheduleRefresh(newExpiration);
    } catch (error) {
      console.error('[Session] Token refresh failed:', error);
      toast.error('Session refresh failed. Please login again.');
      await logout();
      window.location.href = '/auth/login';
    }
  }, [refreshAuth, logout, getTokenExpiration]);

  /**
   * Show session expiration warning
   */
  const showExpirationWarning = useCallback(() => {
    if (sessionInfo.warningShown) return;

    setSessionInfo((prev) => ({ ...prev, warningShown: true }));

    toast.warning('Your session will expire in 10 minutes', {
      description: 'Please save your work. Your session will be refreshed automatically.',
      duration: 10000,
      action: {
        label: 'Refresh Now',
        onClick: () => refreshToken(),
      },
    });

    console.log('[Session] Expiration warning shown');
  }, [sessionInfo.warningShown, refreshToken]);

  /**
   * Schedule proactive token refresh
   */
  const scheduleRefresh = useCallback(
    (expiresAt: number | null) => {
      // Clear existing timers
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
      }

      if (!expiresAt) return;

      const now = Date.now();
      const timeUntilExpiry = expiresAt - now;
      const timeUntilRefresh = timeUntilExpiry - REFRESH_BEFORE_EXPIRY_MS;
      const timeUntilWarning = timeUntilExpiry - WARNING_BEFORE_EXPIRY_MS;

      console.log('[Session] Token expires in:', Math.floor(timeUntilExpiry / 1000 / 60), 'minutes');

      // Schedule warning
      if (timeUntilWarning > 0) {
        warningTimerRef.current = setTimeout(() => {
          showExpirationWarning();
        }, timeUntilWarning);
        console.log('[Session] Warning scheduled in:', Math.floor(timeUntilWarning / 1000 / 60), 'minutes');
      }

      // Schedule refresh
      if (timeUntilRefresh > 0) {
        refreshTimerRef.current = setTimeout(() => {
          refreshToken();
        }, timeUntilRefresh);
        console.log('[Session] Refresh scheduled in:', Math.floor(timeUntilRefresh / 1000 / 60), 'minutes');
      } else {
        // Token is about to expire or already expired, refresh immediately
        console.log('[Session] Token expiring soon, refreshing now...');
        refreshToken();
      }
    },
    [refreshToken, showExpirationWarning]
  );

  /**
   * Track user activity
   */
  const handleActivity = useCallback(() => {
    const now = Date.now();
    setSessionInfo((prev) => ({ ...prev, lastActivity: now }));

    // Debounce activity logging
    if (activityTimerRef.current) {
      clearTimeout(activityTimerRef.current);
    }

    activityTimerRef.current = setTimeout(() => {
      console.log('[Session] User activity detected');
    }, 5000);
  }, []);

  /**
   * Initialize session monitoring
   */
  useEffect(() => {
    if (!isAuthenticated) {
      // Clear all timers when logged out
      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (activityTimerRef.current) clearTimeout(activityTimerRef.current);
      return;
    }

    // Get initial expiration
    const expiration = getTokenExpiration();
    setSessionInfo((prev) => ({
      ...prev,
      expiresAt: expiration,
      lastActivity: Date.now(),
    }));

    // Schedule initial refresh
    if (expiration) {
      scheduleRefresh(expiration);
    }

    // Setup activity listeners
    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    console.log('[Session] Session monitoring initialized');

    return () => {
      // Cleanup
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });

      if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (activityTimerRef.current) clearTimeout(activityTimerRef.current);
    };
  }, [isAuthenticated, getTokenExpiration, scheduleRefresh, handleActivity]);

  /**
   * Sync token changes from localStorage (for multi-tab support)
   */
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'accessToken') {
        if (e.newValue) {
          // Token updated, reschedule
          const newExpiration = getTokenExpiration();
          setSessionInfo((prev) => ({
            ...prev,
            expiresAt: newExpiration,
            warningShown: false,
          }));
          scheduleRefresh(newExpiration);
          console.log('[Session] Token updated from another tab');
        } else {
          // Token removed, logout
          logout();
          console.log('[Session] Token removed from another tab');
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [getTokenExpiration, scheduleRefresh, logout]);

  return {
    sessionInfo,
    refreshToken,
    remainingTime: sessionInfo.expiresAt ? sessionInfo.expiresAt - Date.now() : null,
  };
}
